/**
 * Keyboard Manager - Centralized keyboard shortcut and event handling system
 * Manages global keyboard events, modifier tracking, and keyboard shortcuts
 */

export interface KeyboardModifierState {
  readonly shift: boolean;
  readonly alt: boolean;
  readonly ctrl: boolean;
  readonly meta: boolean;
}

export interface KeyboardShortcut {
  readonly key: string; // The main key (e.g., 's', 'Enter', 'Escape')
  readonly modifiers?: {
    readonly shift?: boolean;
    readonly alt?: boolean;
    readonly ctrl?: boolean;
    readonly meta?: boolean;
  };
  readonly description?: string; // Human-readable description
  readonly handler: (event: KeyboardEvent) => void | boolean; // Return false to prevent default
}

export interface KeyboardManagerConfig {
  readonly onModifierChange?: (modifiers: KeyboardModifierState) => void;
  readonly onKeyDown?: (
    event: KeyboardEvent,
    modifiers: KeyboardModifierState
  ) => void;
  readonly onKeyUp?: (
    event: KeyboardEvent,
    modifiers: KeyboardModifierState
  ) => void;
  readonly shortcuts?: readonly KeyboardShortcut[]; // Initial shortcuts to register
}

export interface KeyboardManagerAPI {
  readonly getModifiers: () => KeyboardModifierState;
  readonly isShiftPressed: () => boolean;
  readonly isAltPressed: () => boolean;
  readonly isCtrlPressed: () => boolean;
  readonly isMetaPressed: () => boolean;
  // Shortcut management
  readonly registerShortcut: (shortcut: KeyboardShortcut) => string; // Returns shortcut ID
  readonly unregisterShortcut: (id: string) => boolean;
  readonly registerShortcuts: (
    shortcuts: readonly KeyboardShortcut[]
  ) => readonly string[];
  readonly getRegisteredShortcuts: () => readonly KeyboardShortcut[];
  readonly clearShortcuts: () => void;
  readonly destroy: () => void;
}

/**
 * Creates a keyboard manager that tracks global keyboard events,
 * modifier state, and handles keyboard shortcuts
 *
 * @param config - Configuration options
 * @returns Keyboard manager API
 *
 * @example
 * ```typescript
 * const keyboardManager = createKeyboardManager({
 *   onModifierChange: (modifiers) => {
 *     console.log('Modifiers changed:', modifiers);
 *     uiState.setShiftPressed(modifiers.shift);
 *     uiState.setAltPressed(modifiers.alt);
 *   },
 *   shortcuts: [
 *     {
 *       key: 's',
 *       modifiers: { meta: true }, // Cmd+S on Mac, Ctrl+S on Windows
 *       description: 'Save project',
 *       handler: () => saveProject()
 *     },
 *     {
 *       key: 'z',
 *       modifiers: { meta: true },
 *       description: 'Undo',
 *       handler: () => undo()
 *     }
 *   ]
 * });
 *
 * // Register shortcuts dynamically
 * keyboardManager.registerShortcut({
 *   key: 'Escape',
 *   description: 'Close modal',
 *   handler: () => closeModal()
 * });
 *
 * // Later...
 * keyboardManager.destroy();
 * ```
 */
export const createKeyboardManager = (
  config: KeyboardManagerConfig = {}
): KeyboardManagerAPI => {
  const { onModifierChange, onKeyDown, onKeyUp, shortcuts = [] } = config;

  let isDestroyed = false;

  // Track modifier state
  let modifierState: KeyboardModifierState = {
    shift: false,
    alt: false,
    ctrl: false,
    meta: false,
  };

  // Shortcut management
  let shortcutIdCounter = 0;
  let registeredShortcuts: Map<string, KeyboardShortcut & { id: string }> =
    new Map();

  /**
   * Update modifier state from keyboard event
   */
  const updateModifierState = (event: KeyboardEvent): void => {
    const newState: KeyboardModifierState = {
      shift: event.shiftKey,
      alt: event.altKey,
      ctrl: event.ctrlKey,
      meta: event.metaKey,
    };

    // Check if modifiers changed
    const hasChanged =
      newState.shift !== modifierState.shift ||
      newState.alt !== modifierState.alt ||
      newState.ctrl !== modifierState.ctrl ||
      newState.meta !== modifierState.meta;

    if (hasChanged) {
      modifierState = newState;
      onModifierChange?.(modifierState);
    }
  };

  /**
   * Check if a shortcut matches the current event and modifiers
   */
  const matchesShortcut = (
    shortcut: KeyboardShortcut,
    event: KeyboardEvent
  ): boolean => {
    // Check if key matches (case insensitive for letters)
    const eventKey = event.key.toLowerCase();
    const shortcutKey = shortcut.key.toLowerCase();

    if (eventKey !== shortcutKey) return false;

    // Check modifiers - if shortcut specifies modifiers, they must match exactly
    const requiredModifiers = shortcut.modifiers || {};

    return (
      (requiredModifiers.shift ?? false) === event.shiftKey &&
      (requiredModifiers.alt ?? false) === event.altKey &&
      (requiredModifiers.ctrl ?? false) === event.ctrlKey &&
      (requiredModifiers.meta ?? false) === event.metaKey
    );
  };

  /**
   * Handle keydown events
   */
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (isDestroyed) return;

    updateModifierState(event);

    // Don't interfere with pure modifier keys (Shift, Alt, Ctrl, Meta by themselves)
    const isModifierOnly = ["Shift", "Alt", "Control", "Meta"].includes(
      event.key
    );

    if (!isModifierOnly) {
      // Check for matching shortcuts only for non-modifier keys
      for (const [id, shortcut] of registeredShortcuts) {
        if (matchesShortcut(shortcut, event)) {
          console.log(
            `🎹 Keyboard shortcut triggered: ${
              shortcut.description || shortcut.key
            }`
          );

          const result = shortcut.handler(event);

          // If handler returns false, don't prevent default
          // Otherwise, prevent default behavior
          if (result !== false) {
            event.preventDefault();
            event.stopPropagation();
          }

          // Only trigger the first matching shortcut
          break;
        }
      }
    }

    onKeyDown?.(event, modifierState);
  };

  /**
   * Handle keyup events
   */
  const handleKeyUp = (event: KeyboardEvent): void => {
    if (isDestroyed) return;

    updateModifierState(event);
    onKeyUp?.(event, modifierState);
  };

  /**
   * Handle window focus/blur to reset modifier state
   * This prevents stuck modifiers when focus is lost
   */
  const handleFocusChange = (): void => {
    if (isDestroyed) return;

    const resetState: KeyboardModifierState = {
      shift: false,
      alt: false,
      ctrl: false,
      meta: false,
    };

    if (
      resetState.shift !== modifierState.shift ||
      resetState.alt !== modifierState.alt ||
      resetState.ctrl !== modifierState.ctrl ||
      resetState.meta !== modifierState.meta
    ) {
      modifierState = resetState;
      onModifierChange?.(modifierState);
    }
  };

  // Set up global event listeners
  if (typeof window !== "undefined") {
    // Don't use passive for keydown since we need to prevent default for shortcuts
    document.addEventListener("keydown", handleKeyDown, { passive: false });
    // Keyup can be passive since we don't prevent default there
    document.addEventListener("keyup", handleKeyUp, { passive: true });
    // Focus events can remain passive
    window.addEventListener("blur", handleFocusChange, { passive: true });
    window.addEventListener("focus", handleFocusChange, { passive: true });
  }

  /**
   * Public API
   */
  const getModifiers = (): KeyboardModifierState => ({ ...modifierState });

  const isShiftPressed = (): boolean => modifierState.shift;
  const isAltPressed = (): boolean => modifierState.alt;
  const isCtrlPressed = (): boolean => modifierState.ctrl;
  const isMetaPressed = (): boolean => modifierState.meta;

  /**
   * Register a keyboard shortcut
   */
  const registerShortcut = (shortcut: KeyboardShortcut): string => {
    const id = `shortcut-${++shortcutIdCounter}`;
    registeredShortcuts.set(id, { ...shortcut, id });
    console.log(
      `🎹 Registered shortcut: ${shortcut.description || shortcut.key} (${id})`
    );
    return id;
  };

  /**
   * Unregister a keyboard shortcut by ID
   */
  const unregisterShortcut = (id: string): boolean => {
    const success = registeredShortcuts.delete(id);
    if (success) {
      console.log(`🎹 Unregistered shortcut: ${id}`);
    }
    return success;
  };

  /**
   * Register multiple shortcuts at once
   */
  const registerShortcuts = (
    shortcuts: readonly KeyboardShortcut[]
  ): readonly string[] => {
    return shortcuts.map((shortcut) => registerShortcut(shortcut));
  };

  /**
   * Get all registered shortcuts
   */
  const getRegisteredShortcuts = (): readonly KeyboardShortcut[] => {
    return Array.from(registeredShortcuts.values());
  };

  /**
   * Clear all registered shortcuts
   */
  const clearShortcuts = (): void => {
    registeredShortcuts.clear();
    console.log("🎹 Cleared all keyboard shortcuts");
  };

  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Remove event listeners
    if (typeof window !== "undefined") {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleFocusChange);
      window.removeEventListener("focus", handleFocusChange);
    }

    // Clear shortcuts and reset state
    registeredShortcuts.clear();
    modifierState = {
      shift: false,
      alt: false,
      ctrl: false,
      meta: false,
    };
  };

  // Register initial shortcuts if provided
  if (shortcuts.length > 0) {
    registerShortcuts(shortcuts);
  }

  return {
    getModifiers,
    isShiftPressed,
    isAltPressed,
    isCtrlPressed,
    isMetaPressed,
    registerShortcut,
    unregisterShortcut,
    registerShortcuts,
    getRegisteredShortcuts,
    clearShortcuts,
    destroy,
  };
};
